import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Globe, Check } from 'lucide-react';

interface GlobalLanguageSelectorProps {
  variant?: 'header' | 'footer' | 'default';
}

export function GlobalLanguageSelector({ variant = 'default' }: GlobalLanguageSelectorProps) {
  const { language, setLanguage, languages } = useLanguage();

  const currentLang = languages.find(l => l.code === language);

  if (variant === 'header') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 text-white/80 hover:text-white hover:bg-white/10 gap-1.5"
          >
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline text-xs font-medium">{currentLang?.nativeName}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 bg-popover border border-border">
          {languages.map((lang) => (
            <DropdownMenuItem 
              key={lang.code} 
              onClick={() => setLanguage(lang.code)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <span className={
                  lang.code === 'hi' ? 'font-hindi' :
                  lang.code === 'pa' ? 'font-punjabi' :
                  lang.code === 'bn' ? 'font-bengali' : ''
                }>
                  {lang.nativeName}
                </span>
                <span className="text-muted-foreground text-xs">({lang.name})</span>
              </span>
              {language === lang.code && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (variant === 'footer') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 text-muted-foreground hover:text-foreground gap-1.5"
          >
            <Globe className="h-4 w-4" />
            <span className="text-xs font-medium">{currentLang?.nativeName}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 bg-popover border border-border">
          {languages.map((lang) => (
            <DropdownMenuItem 
              key={lang.code} 
              onClick={() => setLanguage(lang.code)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <span className={
                  lang.code === 'hi' ? 'font-hindi' :
                  lang.code === 'pa' ? 'font-punjabi' :
                  lang.code === 'bn' ? 'font-bengali' : ''
                }>
                  {lang.nativeName}
                </span>
                <span className="text-muted-foreground text-xs">({lang.name})</span>
              </span>
              {language === lang.code && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Globe className="h-4 w-4" />
          <span>{currentLang?.nativeName}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {languages.map((lang) => (
          <DropdownMenuItem 
            key={lang.code} 
            onClick={() => setLanguage(lang.code)}
            className="flex items-center justify-between cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span className={
                lang.code === 'hi' ? 'font-hindi' :
                lang.code === 'pa' ? 'font-punjabi' :
                lang.code === 'bn' ? 'font-bengali' : ''
              }>
                {lang.nativeName}
              </span>
              <span className="text-muted-foreground text-xs">({lang.name})</span>
            </span>
            {language === lang.code && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
